import React, { useState } from "react";
import "../../assets/fa-icons/css/fontawesome.css";
import "../../assets/fa-icons/css/sharp-solid.css";
import { FontStyles } from "../utils/font-styles.ts";
import { SectionHeader } from "../components/section-header.tsx";
import { focusStyles } from "../utils/focus-styles.ts";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@radix-ui/react-collapsible";
import { usePropertyDetailsContext } from "./contexts/property-details-context.tsx";
import { cn } from "../utils/cn.ts";

const FEET_PER_METRE = 3.28084;
const INCHES_PER_FOOT = 12;

function metresToFeetInches(metres: number): [number, number] {
  const feet = metres * FEET_PER_METRE;
  let wholeFeet = Math.floor(feet);
  let inches = Math.round((feet - wholeFeet) * INCHES_PER_FOOT);
  if (inches === 12) {
    wholeFeet++;
    inches = 0;
  }
  return [wholeFeet, inches];
}

function formatDimensions(width: string | undefined, length: string | undefined): string | null {
  const w = parseFloat(width ?? "");
  const l = parseFloat(length ?? "");
  if (!w || !l) return null;
  const [wFeet, wInches] = metresToFeetInches(w);
  const [lFeet, lInches] = metresToFeetInches(l);
  return `${w.toString()} m X ${l.toString()} m / ${wFeet.toString()}'${wInches.toString()}" X ${lFeet.toString()}'${lInches.toString()}"`;
}

const componentContainer = "flex flex-col  gap-3 md:gap-3.5 w-full";

const contentSubheadingStyle = `${FontStyles["body-s"]} text-light-slate font-medium`;
const contentTextStyle = `${FontStyles["body-md"]} text-slate`;
export const RoomsDescription: React.FC = () => {
  const { rooms } = usePropertyDetailsContext();
  if (!rooms) return null;
  const [openedItems, setOpenedItems] = useState<boolean[]>(rooms.map(() => false));
  return (
    rooms.length > 0 && (
      <div className={componentContainer}>
        <SectionHeader text="Rooms" as="h2" className={FontStyles.h5} />
        {rooms.map((room, index) => {
          const formattedDimensions = formatDimensions(room.width, room.length);
          const hasContent = Boolean(room.description) || formattedDimensions !== null;
          return (
            hasContent && (
              <Collapsible
                key={index}
                open={openedItems[index]}
                className="w-full border border-neutral-200 rounded-md bg-cloud"
                onOpenChange={() => {
                  setOpenedItems((prev) => prev.map((isOpen, i) => (i === index ? !isOpen : isOpen)));
                }}
              >
                <CollapsibleTrigger
                  className={`flex items-center justify-between w-full p-3 md:py-3.5 md:px-5 ${focusStyles}`}
                >
                  <span className={`${FontStyles["body-md"]} text-slate font-semibold`}>{room.name}</span>
                  <i
                    className={`fa-sharp fa-solid ${openedItems[index] ? "fa-angle-up" : "fa-angle-down"} text-light-slate`}
                  />
                </CollapsibleTrigger>
                {/* forceMount keeps each room's description and dimensions in the server-rendered
                    HTML. Radix gates CollapsibleContent's children on `isOpen && children`, so without
                    it the room copy - the most unique text on the page - never reaches the markup at
                    all. forceMount also pins Radix's internal isOpen to true, which stops it applying
                    its own `hidden` attribute, so the collapsed state is hidden via data-state here. */}
                <CollapsibleContent
                  forceMount
                  className="data-[state=closed]:hidden p-3 md:py-3.5 md:px-5 border-t border-neutral-200"
                >
                  <div className={"flex flex-col gap-3.5"}>
                    {room.description && (
                      <div className={"flex flex-col"}>
                        <span className={contentSubheadingStyle}>Description</span>
                        <p className={cn(contentTextStyle, "!whitespace-pre-line")}>{room.description}</p>
                      </div>
                    )}
                    {formattedDimensions && (
                      <div className={"flex flex-col"}>
                        <span className={contentSubheadingStyle}>Dimensions</span>
                        <span className={contentTextStyle}>{formattedDimensions}</span>
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )
          );
        })}
      </div>
    )
  );
};
